import React, {useEffect, useMemo, useRef, useState} from 'react';
import { ConfigProvider } from 'tea-component';
import moment, { Moment } from "moment";
import DateTypeSegment from "@/pages/datalab/components/DateComponents/DateTypeSegment";
import { RangePicker } from '@/pages/home/components/RangePicker/RangePicker';
import { getLastDateRange } from "@/pages/datalab/utils/utils";
import classNames from "classnames";
// import {adjustOverlayPosition} from "@/pages/datalab/utils/overlayPositionAdjuster";
import { START_OF_DATE } from '@/pages/dashboard/utils/_config/constant';
import { DATALAB_DATE_EVENT } from '@dbl/core/global';
import { getLocaleState } from '@home/utils/state';
import DateEventShortcut, { IQuickValue } from '../DateEventShortcut';
import { getDatalabShortcut } from "@/pages/datalab/components/DateComponents/utils";
import { useIntl } from "umi";
import { LANGUAGE_EN } from "@home/_config/config";
import { DateGranularity, DateGranularityText, MomentUnitMap, TDateRange, type TDateGranularity } from '@dbl/core/date';
import TimeRangeShowText from "@/pages/datalab/components/DateComponents/TimeRangeDisplay";
import './index.scss';
import ConfirmAndCancel from "@/pages/datalab/components/ConfirmAndCancle";
import type { TDatalabDateEvent } from '@dbl/core/global';

/**
 * 时间值接口
 */
interface ITimeValue {
  dateRange: string[];
  dateType: TDateGranularity;
  quickValue: IQuickValue;
}

/**
 * 时间值状态接口
 */
interface ITimeValueState {
  type?: [string, string];
  momentRange?: [Moment | null, Moment | null];
  showTime?: boolean;
  dateRange?: string[];
  dateType?: TDateGranularity;
  quickValue?: IQuickValue;
}

/**
 * RangePicker Ref 接口
 */
interface IRangePickerRef {
  setPopActive: () => void;
  changeType: (newType: [string, string]) => void;
  currentType: [string, string];
}

/**
 * 快捷选项配置项接口
 */
interface IShortcutOption {
  name: string;
  nameCN: string;
  type: string;
  value: [Moment, Moment];
  onClick: () => [Moment, Moment];
  timeType?: string;
}

/**
 * handleChangeValue 的 others 参数接口
 */
interface IHandleChangeValueOthers {
  type?: string;
  tab?: string;
  quickValue?: IQuickValue;
}

/**
 * BaseTimePicker 组件 Props 接口
 */
interface IBaseTimePickerProps {
  value?: ITimeValue;
  onChange: (value: ITimeValue) => void;
  className?: string;
  mountClassNames?: string;
  pickerLabelClassName?: string;
}

/**
 * 静态时间范围选择
 * @param props
 * @constructor
 */
function BaseTimePicker(props: IBaseTimePickerProps) {
  const { value = {dateRange: [], dateType: DateGranularity.DAY, quickValue: {tab: 'common'}}, onChange, className, mountClassNames, pickerLabelClassName, ...reset } = props;
  const { data: seasonInfo } = getLocaleState<TDatalabDateEvent>(DATALAB_DATE_EVENT) || {};
  const { locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;
  const rangePickerRef = useRef<IRangePickerRef | null>(null);
  const isConfirmRef = useRef<boolean>(false);
  const [mountNode, setMountNode] = useState<Element | null>(null);
  
  const [timeValue, setTimeValue] = useState<ITimeValueState>({});
  
  const handleInitValue = (value: ITimeValue )=> {
    // 从传入的值中解构出日期范围、日期类型和快速选择值，并设置默认值
    const { dateRange=[], dateType, quickValue } = value;
    // 确定新的日期类型，如果原始类型是'day'或'hour'，则转换为'date'
    const newType = ['day', 'hour'].includes(dateType) ? 'date' : dateType;
    /**
     * 以下逻辑为解决两种场景:
     * 1. 当 Hour面板展示的是 Time选项时, 此时切换到其他日期类型时, 面板展示错误
     * 2. 当动态日期是month类型, 切换到静态日期, 应该转换为默认的Day类型
     */
    if (rangePickerRef.current?.currentType[0] !== newType) {
      rangePickerRef.current?.changeType([newType, newType]);
    }
    // 根据日期类型处理日期值
    // 如果是'hour'类型，则直接使用传入的日期范围
    // 否则，对日期范围的开始和结束应用相应的日期类型处理
    const newDateValue = dateType === 'hour' ?
      [moment(dateRange?.[0]), moment(dateRange?.[1])] :
      [moment(dateRange?.[0])?.startOf(dateType), moment(dateRange?.[1])?.endOf(dateType)];
    // 设置时间相关的状态值，包括类型、日期值、是否显示时间、原始日期范围、日期类型和比较时间
    setTimeValue({
      type: [newType, newType],       // 日期类型数组
      momentRange: newDateValue,                   // 处理后的日期值数组
      showTime: dateType === 'hour',  // 是否显示时间选择器
      dateRange,                      // 原始日期范围
      dateType,                       // 日期类型
      quickValue, // 设置快速选择值
    });
  };
  
  useEffect(() => {
    handleInitValue(value);
  }, [value]);
  
  const newType = timeValue?.dateType === 'day' ? 'date' : timeValue?.dateType;
  const commonDataList = useMemo(()=> {
    const commonDataListObj: Record<string, IShortcutOption[]> = getDatalabShortcut(START_OF_DATE, {}) || {};
    return commonDataListObj?.[newType];
  },[newType]);
  
  return (
    <ConfigProvider locale={isEn ? "en" : "zh"}>
      <RangePicker
        ref={rangePickerRef}
        className={classNames('dbl-base-time-picker', className)}
        header={(<>
          <DateTypeSegment isEn={isEn} value={timeValue?.dateType} onChange={(newDateType: TDateGranularity) => {
            let newDateRange: string[] = [];
            if (newDateType === 'hour') {
              newDateRange = [
                moment().startOf('day').format('YYYY-MM-DD HH:mm:ss'),
                moment().endOf('day').format('YYYY-MM-DD HH:mm:ss'),
              ];
            } else {
              newDateRange = getLastDateRange(newDateType === 'day' ? 7 : 1, newDateType);
            }
            const newType = ['day', 'hour'].includes(newDateType) ? 'date' : newDateType;
            /**
             * 以下逻辑为解决两种场景:
             * 1. 当 Hour面板展示的是 Time选项时, 此时切换到其他日期类型时, 面板展示错误
             * 2. 当动态日期是month类型, 切换到静态日期, 应该转换为默认的Day类型
             */
            if (rangePickerRef.current?.currentType[0] !== newType) {
              rangePickerRef.current?.changeType([newType, newType]);
            }

            const momentUnit = MomentUnitMap[newDateType] as moment.unitOfTime.StartOf;
            setTimeValue((timeValue) => ({
              ...timeValue,
              momentRange: newDateType === 'hour' ?
                [moment(newDateRange?.[0]), moment(newDateRange?.[1])] :
                [
                  moment(newDateRange?.[0])?.startOf(momentUnit) || null,
                  moment(newDateRange?.[1])?.endOf(momentUnit) || null
                ],
              dateRange: newDateRange,
              dateType: newDateType,
              type: [newType, newType],
              showTime: newDateType === 'hour'
            }));
            
          }}/>
          <DateEventShortcut
            startOfTime={START_OF_DATE}
            latestDate={{}}
            commonDataList={commonDataList}
            type={value.dateType === 'day' ? 'date' : value.dateType}
            seasonInfo={seasonInfo}
            quickValue={timeValue?.quickValue || {tab: 'common'}}
            changeQuickValue={(quickValue: IQuickValue)=> {
              setTimeValue((timeValue) => ({
                ...timeValue,
                quickValue,
              }));
            }}
            handleChangeValue={(dates: Moment[], others?: IHandleChangeValueOthers) => {
              const {type, tab, quickValue} = others || {};
              const dateRange = dates?.map((item: moment.MomentInput) => moment(item).format(type === 'hour' ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD'));
              // 选择事件切换到日粒度面板
              if (tab === 'event') {
                rangePickerRef.current?.changeType(['date', 'date']);
              }
              // 选择事件切换为日粒度
              setTimeValue((timeValue) => ({
                ...timeValue,
                ...(tab === 'event' ? {
                  dateType: DateGranularity.DAY,
                  type: ['date', 'date'],
                  showTime: false,
                } : {}),
                momentRange: [
                  dateRange?.[0] ? moment(dateRange[0]) : null,
                  dateRange?.[1] ? moment(dateRange[1]) : null
                ],
                dateRange,
                quickValue: quickValue || timeValue?.quickValue,
              }));
            }}
          />
        </>)}
        calendarType={timeValue?.type?.[0] || 'date'}
        value={timeValue?.momentRange || []}
        startOfWeek={1}
        showTime={timeValue?.showTime}
        onChange={(newValue: [Moment | null, Moment | null]) => {
          
          let dateRange: string[] = [];
          if (timeValue?.dateType === 'hour') {
            dateRange = [
              newValue[0]?.format('YYYY-MM-DD HH:mm:ss') || '',
              newValue[1]?.format('YYYY-MM-DD HH:mm:ss') || '',
            ];
          } else {
            dateRange = [
              newValue[0]?.startOf(MomentUnitMap[timeValue?.dateType || 'day'])?.format('YYYY-MM-DD') || '',
              newValue[1]?.endOf(MomentUnitMap[timeValue?.dateType || 'day'])?.format('YYYY-MM-DD') || '',
            ];
          }
          
          setTimeValue((timeValue) => ({
            ...timeValue,
            momentRange: newValue,
            dateRange,
            quickValue: {
              tab: timeValue?.quickValue?.tab || 'common',
            }
          }));
          
        }}
        overlayClassName={'dbl-base-time-picker-overlay'}
        label={(
          <TimeRangeShowText
            className={classNames('dbl-base-time-picker-label', pickerLabelClassName)}
            prefix={DateGranularityText[value?.dateType || 'day']?.[isEn ? 'en' : 'cn']}
            timeRange={value?.dateRange as TDateRange || []}
            granularity={value?.dateType}
          />
        )}
        changeClose={false}
        popupContainer={mountNode}
        onOpenChange={(open: boolean) => {
          if (open) {
            isConfirmRef.current = false;
            !mountNode && mountClassNames && setMountNode(document.querySelector(mountClassNames) as Element);
          } else {
            if (!isConfirmRef.current) { // 如果用户没有点击确认按钮就关闭了弹窗, 那么状态要恢复到初始态.
              handleInitValue(value);
            }
          }
        }}
        footerConfig={{
          left: (<ConfirmAndCancel
            onConfirm={() => {
              isConfirmRef.current = true;
              onChange({dateRange: timeValue?.dateRange || [], dateType: timeValue?.dateType || 'day', quickValue: timeValue?.quickValue || {tab: 'common'}});
              rangePickerRef.current?.setPopActive();
              
            }}
            onCancel={() => {
              rangePickerRef.current?.setPopActive();
            }}
          />)
        }}
        {...reset}
      />
    </ConfigProvider>
  );
}

export default BaseTimePicker;